import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  Text,
  Banner,
  Box,
  BlockStack,
  InlineStack
} from '@shopify/polaris';

import apiClient from '../utils/api';
import { validateEmail, validatePassword } from '../utils/inputSecurity';

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Enhanced input validation and sanitization
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      setLoading(false);
      return;
    }
    
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      // For login, we only check if password exists, not strength requirements
      if (!password || password.trim().length === 0) {
        setError('Password is required');
        setLoading(false);
        return;
      }
    }

    try {
      // Use sanitized email for login
      const data = await apiClient.login(emailValidation.sanitized, password);
      // Let the parent component handle token setting to avoid double setting
      onLogin(data.token, data.user);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Layout>
        <Layout.Section>
          <Box paddingInline="space-0" paddingBlock="space-800">
            <InlineStack align="center">
              <Box maxWidth="400px" width="100%">
                <Card>
                  <Box paddingInline="space-600" paddingBlock="space-600">
                    <BlockStack gap="space-500">
                      <BlockStack gap="space-200" align="center">
                        <Text variant="headingLg" as="h1">
                          2W Admin Dashboard
                        </Text>
                        <Text variant="bodyMd" tone="subdued">
                          Sign in to manage billing configuration
                        </Text>
                      </BlockStack>

                      {error && (
                        <Banner tone="critical">
                          {error}
                        </Banner>
                      )}

                      <form onSubmit={handleSubmit}>
                        <FormLayout>
                          <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={setEmail}
                            autoComplete="email"
                            required
                          />
                          <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={setPassword}
                            autoComplete="current-password"
                            required
                          />
                          <Button
                            variant="primary"
                            submit
                            loading={loading}
                            fullWidth
                          >
                            Sign In
                          </Button>
                        </FormLayout>
                      </form>

                      <Box paddingBlockStart="space-400">
                        <BlockStack gap="space-200">
                          <Text variant="bodySm" tone="subdued" alignment="center">
                            Access restricted to 2W staff only
                          </Text>
                        </BlockStack>
                      </Box>
                    </BlockStack>
                  </Box>
                </Card>
              </Box>
            </InlineStack>
          </Box>
        </Layout.Section>
      </Layout>
    </Page>
  );
}